import { useQuery } from '@tanstack/react-query';
import type { LedgerHistoryAction, LedgerHistoryEvent } from '@goyone/shared';
import { api } from '../lib/api';
import { longDay, time } from '../lib/format';
import { Icon, type IconName } from './Icon';
import { ErrorState } from './ui';

/**
 * سجل تعديلات قيد أو حساب في الدفتر العام.
 *
 * غير الخطّ الزمني لملفات العملاء (`RecordTimeline`): ذاك يجمع الأنشطة
 * والمرفقات وتغيّر حقول قليلة، وهذا يحكي تغييرًا محاسبيًا قد يمسّ ثلاثين
 * بندًا دفعة واحدة. فيُعرض كل حدث باليوم والساعة صريحين — لا «منذ ساعتين»
 * — لأن السؤال عنه يأتي في مراجعة بعد أسابيع: «من غيّر هذا القيد يوم كذا؟».
 *
 * والقيم تصل نصوصًا مقروءة كما كانت وقت التغيير، فلا تُترجم هنا.
 */

const ACTION_ICONS: Record<LedgerHistoryAction, IconName> = {
  create: 'plus',
  update: 'edit',
  post: 'check',
  reverse: 'refresh',
  delete: 'trash',
  void: 'x',
  legacy: 'clock',
};

export function LedgerHistory({
  path,
  queryKey,
}: {
  /** مسار السجل: `/accounting/journal/:id/history` أو `/accounting/accounts/:id/history`. */
  path: string;
  queryKey: readonly unknown[];
}) {
  const q = useQuery({
    queryKey,
    queryFn: () => api<LedgerHistoryEvent[]>(path),
  });

  if (q.isPending) return <div className="skeleton" style={{ height: 120 }} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (q.data.length === 0) return <p className="mute small center">لا أحداث بعد.</p>;

  return (
    <div className="timeline">
      {q.data.map((e, i) => (
        <HistoryRow key={e.id} event={e} index={Math.min(i, 6)} />
      ))}
    </div>
  );
}

function HistoryRow({ event, index }: { event: LedgerHistoryEvent; index: number }) {
  return (
    <div className="tl-item tl-item-icon reveal" style={{ ['--reveal-i' as string]: index }}>
      <span
        className="tl-dot"
        data-kind={event.action === 'create' || event.action === 'legacy' ? 'created' : 'field'}
      >
        <Icon name={ACTION_ICONS[event.action]} size={13} />
      </span>

      <div className="tl-body">
        <div className="tl-line">
          <strong>{event.summary}</strong>
        </div>

        <div className="tiny mute tl-meta">
          <span>{event.actorName ?? (event.actorId ? 'مستخدم محذوف' : 'النظام')}</span>
          <span>·</span>
          <span>{longDay(event.at)}</span>
          <span className="num">{time(event.at)}</span>
        </div>

        {event.note && <div className="tiny mute tl-detail">{event.note}</div>}

        {event.changes.length > 0 && (
          <ul className="tl-detail small" style={{ margin: 0, paddingInlineStart: 18 }}>
            {event.changes.map((c, i) => (
              <li key={i}>
                <span className="mute">{c.label}:</span>{' '}
                {c.from && (
                  <>
                    <span className="tl-old">{c.from}</span> <span className="mute">←</span>{' '}
                  </>
                )}
                <strong>{c.to ?? <span className="mute">(فارغ)</span>}</strong>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
